import {
  downloadPublishedProjectFileFromCandidates,
  fetchPublishedMapBin,
  fetchSharedResourceBytes,
} from '@/features/published-project/api'
import { parseProjectsBinFromBytes } from '@/features/project-disk/parseProjectsBin'
import { readMallNewBin } from '@/features/project-mall/runtime'
import {
  buildVirtualResourcesFromMap,
  findMapEntry,
  parsePublishedMapManifest,
} from '@/features/published-project/mapManifest'
import {
  cachePublishedBinsToOss,
  cachePublishedPassthroughMapToOss,
  collectPublishedPassthroughMap,
  collectPublishedBinsForCache,
} from '@/features/published-project/cachePublishedBins'
import {
  buildPublishedProjectMainBinCandidates,
  type PublishedProjectVersion3Item,
} from '@/features/published-project/publishedProjectHelpers'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import { serializeVirtualResources } from '@/features/virtual-resource/persistence'
import {
  writeProjectInfoToOss,
} from '@/features/project-cloud/ossSave'
import {
  PROJECT_CURRENT_RES_JSON,
} from '@/features/project-cloud/ossPaths'
import {
  normalizeProjectFontResources,
  type ProjectFontFallbackNotice,
} from '@/features/project-font/runtime'
import type MainProject from '@/model/MainProject'
import type DataFileMap from '@/model/Resource/DataFileMap'
import BrowserUtil from '@/platform/oss/browserUtil'

export interface OpenPublishedProjectProgress {
  percent: number
  message: string
}

export interface OpenPublishedProjectResult {
  project: MainProject
  roots: Record<ResourceCategoryKey, DataFileMap | null>
  fontFallbackNotice: OpenPublishedProjectFontFallbackNotice | null
}

export type OpenPublishedProjectFontFallbackNotice = ProjectFontFallbackNotice

/** 下载服务器发布版本并恢复为当前编辑器可打开的 MainProject 与虚拟资源树。 */
export async function openPublishedProjectVersion(params: {
  guid: string
  uid: string | number
  sessionId: string
  toolVersion: string
  version: PublishedProjectVersion3Item
  onProgress?: (progress: OpenPublishedProjectProgress) => void
}): Promise<OpenPublishedProjectResult> {
  const publishVersion = String(params.version?.version || '').trim()
  if (!params.guid.trim() || !publishVersion) {
    throw new Error(`打开已发布作品参数缺失：guid=${params.guid || '空'}，version=${publishVersion || '空'}`)
  }
  const emit = (percent: number, message: string) => params.onProgress?.({ percent, message })
  emit(8, '下载工程主包…')
  const projectBinBytes = await downloadPublishedProjectFileFromCandidates({
    guid: params.guid,
    sessionId: params.sessionId,
    toolVersion: publishVersion,
    objectNames: buildPublishedProjectMainBinCandidates(params.guid, publishVersion),
  })

  emit(18, '解析工程数据…')
  const project = await parseProjectsBinFromBytes(projectBinBytes, (pct) => {
    emit(18 + pct * 0.22, '解析工程数据…')
  })

  emit(42, '下载发布清单…')
  const mapEntries = params.version.mapEntries
    ?? parsePublishedMapManifest(
      await fetchPublishedMapBin(params.guid, publishVersion),
    )

  const mallEntry = findMapEntry(mapEntries, 'Data/MallNew.bin')
  if (!mallEntry) {
    throw new Error('当前版本缺少 Data/MallNew.bin，无法作为 3.0 工程打开')
  }
  emit(44, '下载商城数据…')
  const mallBytes = await fetchSharedResourceBytes(mallEntry.md5)
  readMallNewBin(mallBytes, project)

  emit(54, '构建资源管理器…')
  const roots = buildVirtualResourcesFromMap(mapEntries)
  emit(58, '解析字体信息…')
  const normalizedFont = await normalizeProjectFontResources(project, roots.font)
  roots.font = normalizedFont.root
  const fontFallbackNotice = normalizedFont.fallbackNotice

  emit(80, '缓存工程文件…')
  const bins = await collectPublishedBinsForCache({
    guid: params.guid,
    version: publishVersion,
    sessionId: params.sessionId,
    toolVersion: publishVersion,
    entries: mapEntries,
    projectBinBytes,
  })
  await cachePublishedBinsToOss({
    guid: params.guid,
    uid: params.uid,
    bins,
    onProgress: (current, total) => {
      emit(80 + (current / Math.max(total, 1)) * 16, '缓存工程文件…')
    },
  })
  await cachePublishedPassthroughMapToOss({
    guid: params.guid,
    uid: params.uid,
    entries: collectPublishedPassthroughMap(mapEntries),
  })

  const oss = BrowserUtil.getInstance().connect(params.uid)
  await oss.writeData(`${params.guid}/${PROJECT_CURRENT_RES_JSON}`, serializeVirtualResources(roots), {
    contentType: 'application/json;charset=utf-8',
  })
  await writeProjectInfoToOss({
    project,
    uid: params.uid,
    publishGindex: params.version.gindex,
  })

  emit(98, '准备进入编辑器…')
  return { project, roots, fontFallbackNotice }
}
